"use client"

import { useState, useCallback, useEffect, useRef } from "react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { formatValue, isDualMeasurementUnit } from "@/lib/utils"
import { getSafeGameItemDescription } from "@/lib/game-item-description"
import {
  ChevronUp,
  ChevronDown,
  Home,
  X,
  Check,
  Calendar,
  Clock,
  Loader2,
  Trophy,
  Volume2,
  VolumeX,
} from "lucide-react"
import { ShareButton } from "@/components/share/share-button"
import { GameItemImage } from "@/components/game/game-item-image"
import { HousingFacts } from "@/components/game/housing-facts"
import {
  preloadImage,
  preloadImages,
  waitForImageReady,
} from "@/lib/preload-image"
import { useCountAnimation } from "@/lib/use-count-animation"
import { playSelectSound, playCorrectSound, playWrongSound, playGameOverSound, playVictorySound, isSoundMuted, setSoundMuted } from "@/lib/sounds"
import { trackProductEvent } from "@/lib/analytics/client"

interface GameItem {
  id: string
  name: string
  image: string | null
  images?: string[]
  value: number
  description: string | null
  floorAreaM2?: number
  houseType?: string
  housingLocation?: string
}

interface GameType {
  id: string
  name: string
  slug?: string
  valueLabel: string
  valueUnit: string | null
  valuePrefix: boolean
  category: {
    name: string
    slug: string
  }
}

interface DailyGameProps {
  gameType: GameType
  items: GameItem[]
  date: string
}

const categoryGradients: Record<string, { from: string; to: string }> = {
  technology: { from: "from-emerald-900/80", to: "to-emerald-950/90" },
  entertainment: { from: "from-rose-900/80", to: "to-rose-950/90" },
  gaming: { from: "from-orange-900/80", to: "to-rose-950/90" },
  "real-estate": { from: "from-amber-900/80", to: "to-amber-950/90" },
  sports: { from: "from-sky-900/80", to: "to-sky-950/90" },
  music: { from: "from-rose-900/80", to: "to-red-950/90" },
  cars: { from: "from-orange-900/80", to: "to-orange-950/90" },
  geography: { from: "from-teal-900/80", to: "to-teal-950/90" },
  movies: { from: "from-sky-900/80", to: "to-slate-950/90" },
  "social-media": { from: "from-pink-900/80", to: "to-pink-950/90" },
  "dutch-houses": { from: "from-amber-900/80", to: "to-amber-950/90" },
}
const defaultGradient = { from: "from-zinc-800/80", to: "to-zinc-900/90" }
const ROUND_REVEAL_DURATION_MS = 2300
const FAIL_REVEAL_DURATION_MS = 3500

type GameState = "playing" | "correct" | "wrong" | "gameover" | "completed"

export function DailyGame({ gameType, items, date }: DailyGameProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [score, setScore] = useState(0)
  const [gameState, setGameState] = useState<GameState>("playing")
  const [revealedValue, setRevealedValue] = useState<number | null>(null)
  const [countdown, setCountdown] = useState("")
  const [nextPanelOpacity, setNextPanelOpacity] = useState(1)
  const [perfectRun, setPerfectRun] = useState(false)
  const [muted, setMutedState] = useState(false)
  const gameStartedAtRef = useRef(Date.now())

  const totalRounds = items.length - 1

  useEffect(() => {
    setMutedState(isSoundMuted())
    gameStartedAtRef.current = Date.now()
    void preloadImage(items[2]?.image, "high")
    void preloadImages(items.slice(3, 6).map((item) => item?.image))
    trackProductEvent("daily_started", {
      gameTypeId: gameType.id,
      properties: {
        date: date.split("T")[0],
        category_slug: gameType.category.slug,
        game_slug: gameType.slug || null,
        rounds: items.length - 1,
      },
    })
  }, [date, gameType.category.slug, gameType.id, gameType.slug, items])

  const toggleMute = () => {
    const next = !muted
    setMutedState(next)
    setSoundMuted(next)
  }

  const animatedValue = useCountAnimation(revealedValue, 800)

  const dateStr = new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  })

  const storageKey = `daily-${date.split("T")[0]}`

  // Check if already completed
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey)
      if (!saved) return
      const data = JSON.parse(saved)
      if (typeof data?.score !== "number") return
      setScore(data.score)
      setPerfectRun(data.perfect === true)
      setGameState("completed")
    } catch {
      // Let the player play again when saved state is corrupt
    }
  }, [storageKey])

  // Countdown timer
  useEffect(() => {
    if (gameState !== "gameover" && gameState !== "completed") return

    const update = () => {
      const now = new Date()
      const tomorrow = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1))
      const diff = tomorrow.getTime() - now.getTime()
      const hours = Math.floor(diff / 3600000)
      const minutes = Math.floor((diff % 3600000) / 60000)
      const seconds = Math.floor((diff % 60000) / 1000)
      setCountdown(`${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`)
    }

    update()
    const interval = setInterval(update, 1000)
    return () => clearInterval(interval)
  }, [gameState])

  const currentItem = items[currentIndex]
  const nextItem = items[currentIndex + 1]

  useEffect(() => {
    preloadImages([
      ...(currentItem?.images?.slice(1) ?? []),
      ...(nextItem?.images?.slice(1) ?? []),
    ])
  }, [currentItem, nextItem])

  const handleGuess = useCallback((guess: "higher" | "lower") => {
    if (!currentItem || !nextItem || gameState !== "playing") return

    playSelectSound()

    const isCorrect =
      guess === "higher"
        ? nextItem.value >= currentItem.value
        : nextItem.value <= currentItem.value

    trackProductEvent("daily_answered", {
      gameTypeId: gameType.id,
      properties: {
        date: date.split("T")[0],
        guess,
        correct: isCorrect,
        round: currentIndex + 1,
        score_after: isCorrect ? score + 1 : score,
      },
    })

    setRevealedValue(nextItem.value)

    if (isCorrect) {
      setGameState("correct")
      const newScore = score + 1
      setScore(newScore)
      playCorrectSound()
      // The immediately following panel was warmed when the current round
      // started, so use the reveal window to stay one full round ahead.
      void preloadImage(items[currentIndex + 2]?.image, "high")
      void preloadImages(items[currentIndex + 2]?.images?.slice(1) ?? [])
      void preloadImage(items[currentIndex + 3]?.image, "high")

      setTimeout(async () => {
        if (currentIndex + 2 >= items.length) {
          // Finishing every round is a win, not a game over
          setPerfectRun(true)
          setGameState("gameover")
          trackProductEvent("daily_completed", {
            gameTypeId: gameType.id,
            properties: {
              date: date.split("T")[0],
              score: newScore,
              perfect: true,
              duration_seconds: Math.round(
                (Date.now() - gameStartedAtRef.current) / 100,
              ) / 10,
            },
          })
          playVictorySound()
          try {
            localStorage.setItem(storageKey, JSON.stringify({ score: newScore, perfect: true }))
          } catch {}
        } else {
          await waitForImageReady(items[currentIndex + 2]?.image)
          // Brief crossfade
          setNextPanelOpacity(0)
          setTimeout(() => {
            setCurrentIndex((prev) => prev + 1)
            setRevealedValue(null)
            setGameState("playing")
            requestAnimationFrame(() => setNextPanelOpacity(1))
          }, 200)
        }
      }, ROUND_REVEAL_DURATION_MS)
    } else {
      setGameState("wrong")
      playWrongSound()
      setTimeout(() => {
        setGameState("gameover")
        trackProductEvent("daily_completed", {
          gameTypeId: gameType.id,
          properties: {
            date: date.split("T")[0],
            score,
            perfect: false,
            duration_seconds: Math.round(
              (Date.now() - gameStartedAtRef.current) / 100,
            ) / 10,
          },
        })
        playGameOverSound()
        try {
          localStorage.setItem(storageKey, JSON.stringify({ score }))
        } catch {}
      }, FAIL_REVEAL_DURATION_MS)
    }
  }, [currentItem, nextItem, gameState, score, currentIndex, items, storageKey, date, gameType.id])

  // Keyboard controls: arrows or H/L to guess
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return
      if (gameState !== "playing") return

      const key = e.key.toLowerCase()
      if (e.key === "ArrowUp" || key === "h") {
        e.preventDefault()
        handleGuess("higher")
      } else if (e.key === "ArrowDown" || key === "l") {
        e.preventDefault()
        handleGuess("lower")
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [gameState, handleGuess])

  const displayValue = (value: number) => {
    return formatValue(value, gameType.valueUnit || undefined, gameType.valuePrefix)
  }
  const valueTextSize = isDualMeasurementUnit(gameType.valueUnit)
    ? "text-2xl sm:text-3xl md:text-4xl"
    : "text-4xl md:text-5xl"


  // Completed state
  if (gameState === "completed") {
    return (
      <div className="studio-game-stage min-h-dvh flex flex-col bg-zinc-950">
        <header className="fixed top-0 left-0 right-0 z-50 p-4 bg-zinc-950/80 backdrop-blur-xl">
          <div className="container mx-auto flex items-center justify-between">
            <Link href="/">
              <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10 rounded-full gap-2">
                <Home className="h-4 w-4" />
                <span className="hidden sm:inline">Home</span>
              </Button>
            </Link>
            <div className="flex items-center gap-2 rounded-full border border-[#d8d0c4] bg-[#fbf7ef] px-4 py-2 shadow-[0_8px_20px_rgba(70,55,43,0.08)]">
              <Calendar className="h-4 w-4 text-[#b83f34]" />
              <span className="text-sm font-semibold text-[#3f3832]">{dateStr}</span>
            </div>
          </div>
        </header>

        <div className="flex-1 flex items-center justify-center pt-20 px-4">
          <div className="studio-game-card bg-zinc-900 rounded-3xl p-8 md:p-12 max-w-md w-full text-center border border-zinc-800">
            <div className="mx-auto mb-8 flex h-20 w-20 items-center justify-center rounded-full border border-[#ff6f61]/35 bg-[#ff6f61]/15">
              {perfectRun ? (
                <Trophy className="h-10 w-10 text-[#b83f34]" />
              ) : (
                <Check className="h-10 w-10 text-[#b83f34]" />
              )}
            </div>
            <h2 className="mb-3 text-3xl font-bold text-[#171717]">Already Completed!</h2>
            <p className="mb-4 font-medium text-[#544c43]">
              You scored <span className="font-bold text-[#171717]">{score} / {totalRounds}</span> on today&apos;s daily challenge.
              {perfectRun && <span className="font-bold text-[#b83f34]"> Perfect run!</span>}
            </p>
            <p className="mb-6 font-medium text-[#655d54]">{gameType.name}</p>

            <div className="mb-8 flex items-center justify-center gap-2 font-medium text-[#544c43]">
              <Clock className="w-4 h-4" />
              <span className="text-sm">Next challenge in {countdown}</span>
            </div>

            <div className="flex flex-col gap-3">
              <ShareButton
                type="daily"
                score={score}
                gameType={gameType.name}
                extra={{ date: dateStr }}
              />
              <Link href="/play" className="w-full">
                <Button variant="outline" className="studio-game-primary-action h-14 w-full rounded-xl text-lg">
                  Play More Games
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Game over state
  if (gameState === "gameover") {
    return (
      <div className="studio-game-stage min-h-dvh flex flex-col bg-zinc-950">
        <header className="fixed top-0 left-0 right-0 z-50 p-4 bg-zinc-950/80 backdrop-blur-xl">
          <div className="container mx-auto flex items-center justify-between">
            <Link href="/">
              <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10 rounded-full gap-2">
                <Home className="h-4 w-4" />
                <span className="hidden sm:inline">Home</span>
              </Button>
            </Link>
            <div className="flex items-center gap-2 rounded-full border border-[#d8d0c4] bg-[#fbf7ef] px-4 py-2 shadow-[0_8px_20px_rgba(70,55,43,0.08)]">
              <Calendar className="h-4 w-4 text-[#b83f34]" />
              <span className="text-sm font-semibold text-[#3f3832]">{dateStr}</span>
            </div>
          </div>
        </header>

        <div className="flex-1 flex items-center justify-center pt-20 px-4">
          <div className="studio-game-card bg-zinc-900 rounded-3xl p-8 md:p-12 max-w-md w-full text-center border border-zinc-800">
            <div className="mx-auto mb-8 flex h-20 w-20 items-center justify-center rounded-full border border-[#ff6f61]/35 bg-[#ff6f61]/15">
              {perfectRun ? (
                <Trophy className="h-10 w-10 text-[#b83f34]" />
              ) : (
                <Calendar className="h-10 w-10 text-[#b83f34]" />
              )}
            </div>
            <h2 className="mb-3 text-3xl font-bold text-[#171717]">{perfectRun ? "Perfect Run!" : "Daily Complete!"}</h2>
            <p className="mb-2 font-medium text-[#544c43]">{dateStr} &middot; {gameType.name}</p>

            <div className="my-8">
              <div className="text-6xl font-black text-[#171717]">{score} <span className="text-2xl font-bold text-[#544c43]">/ {totalRounds}</span></div>
              <div className="mt-1 text-sm font-bold uppercase tracking-wide text-[#544c43]">{perfectRun ? "Every Round Correct" : "Final Score"}</div>
            </div>

            <div className="mb-8 flex items-center justify-center gap-2 font-medium text-[#544c43]">
              <Clock className="w-4 h-4" />
              <span className="text-sm">Next challenge in {countdown}</span>
            </div>

            <div className="flex flex-col gap-3">
              <ShareButton
                type="daily"
                score={score}
                gameType={gameType.name}
                extra={{ date: dateStr }}
              />
              <Link href="/play" className="w-full">
                <Button variant="outline" className="studio-game-primary-action h-14 w-full rounded-xl text-lg">
                  Play More Games
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!currentItem || !nextItem) {
    return (
      <div className="studio-game-stage min-h-dvh flex items-center justify-center bg-zinc-950">
        <Loader2 className="w-8 h-8 text-[#b83f34] animate-spin" />
      </div>
    )
  }

  const gradient = categoryGradients[gameType.category.slug] || defaultGradient
  const currentDescription = getSafeGameItemDescription({
    description: currentItem.description,
    value: currentItem.value,
    valueLabel: gameType.valueLabel,
    valueUnit: gameType.valueUnit,
    valuePrefix: gameType.valuePrefix,
  })
  const nextDescription = getSafeGameItemDescription({
    description: nextItem.description,
    value: nextItem.value,
    valueLabel: gameType.valueLabel,
    valueUnit: gameType.valueUnit,
    valuePrefix: gameType.valuePrefix,
  })
  const currentIsHousing = Boolean(currentItem.floorAreaM2 && currentItem.houseType)
  const nextIsHousing = Boolean(nextItem.floorAreaM2 && nextItem.houseType)

  return (
    <div className="studio-game-stage min-h-dvh flex flex-col bg-zinc-950">
      <h1 className="sr-only">{gameType.name} Daily Higher or Lower Challenge for {dateStr}</h1>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 p-4 bg-zinc-950/80 backdrop-blur-xl">
        <div className="container mx-auto flex items-center justify-between">
          <Link href="/">
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10 rounded-full gap-2">
              <Home className="h-4 w-4" />
              <span className="hidden sm:inline">Home</span>
            </Button>
          </Link>

          <div className="flex items-center gap-2 rounded-full border border-[#d8d0c4] bg-[#fbf7ef] px-4 py-2 shadow-[0_8px_20px_rgba(70,55,43,0.08)]">
            <Calendar className="h-4 w-4 text-[#b83f34]" />
            <span className="text-sm font-semibold text-[#3f3832]">{dateStr}</span>
            <span className="hidden text-sm text-[#8f867b] sm:inline">•</span>
            <span className="hidden text-sm font-semibold text-[#3f3832] sm:inline">Round {Math.min(currentIndex + 1, totalRounds)} / {totalRounds}</span>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={toggleMute}
              aria-label={muted ? "Unmute sounds" : "Mute sounds"}
              className="w-9 h-9 flex items-center justify-center rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-colors"
            >
              {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
            <div className="text-right">
              <div className="text-3xl font-black text-white">{score}</div>
              <div className="text-xs text-white/40 uppercase tracking-wide">Score</div>
            </div>
          </div>
        </div>
      </header>

      {/* Game Area */}
      <div className="flex-1 flex flex-col md:flex-row pt-20">
        {/* Current Item */}
        <div
          className="flex-1 relative flex items-center justify-center p-6 md:p-12 overflow-hidden"
        >
          <GameItemImage
            src={currentItem.image}
            sources={currentItem.images}
            itemName={currentItem.name}
            gameTypeName={gameType.name}
            gameTypeSlug={gameType.slug}
            categorySlug={gameType.category.slug}
            fallbackClassName={`bg-gradient-to-br ${gradient.from} ${gradient.to}`}
            priority
          />
          <div className="relative z-10 w-full max-w-xl text-center text-white">
            {!currentIsHousing && (
              <>
                <div className="mb-4 text-sm uppercase tracking-widest text-white/50">Current</div>
                <h2 className="mb-3 text-2xl font-bold md:text-4xl">{currentItem.name}</h2>
                {currentDescription && (
                  <p className="mb-6 text-sm text-white/60 md:text-base">{currentDescription}</p>
                )}
              </>
            )}
            <HousingFacts
              floorAreaM2={currentItem.floorAreaM2}
              houseType={currentItem.houseType}
              location={currentItem.housingLocation}
              statusLabel="Current property"
            />
            <div className="inline-block px-8 py-4 rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20">
              <div className="text-sm text-white/60 mb-1">{gameType.valueLabel}</div>
              <div className={`${valueTextSize} font-black`}>{displayValue(currentItem.value)}</div>
            </div>
          </div>
        </div>

        {/* VS Divider */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 hidden md:flex">
          <div className={`w-20 h-20 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 ${
            gameState === "correct" ? "bg-emerald-500 scale-110"
              : gameState === "wrong" ? "bg-rose-500 scale-110"
              : "bg-white"
          }`}>
            {gameState === "correct" ? (
              <Check className="h-10 w-10 text-white" />
            ) : gameState === "wrong" ? (
              <X className="h-10 w-10 text-white" />
            ) : (
              <span className="text-2xl font-black text-zinc-900">VS</span>
            )}
          </div>
        </div>

        {/* Mobile VS */}
        <div className="md:hidden flex justify-center -my-6 z-20">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center shadow-xl transition-all duration-300 ${
            gameState === "correct" ? "bg-emerald-500"
              : gameState === "wrong" ? "bg-rose-500"
              : "bg-white"
          }`}>
            {gameState === "correct" ? (
              <Check className="h-7 w-7 text-white" />
            ) : gameState === "wrong" ? (
              <X className="h-7 w-7 text-white" />
            ) : (
              <span className="text-lg font-black text-zinc-900">VS</span>
            )}
          </div>
        </div>

        {/* Next Item */}
        <div
          className="flex-1 relative flex items-center justify-center p-6 md:p-12 overflow-hidden transition-opacity duration-200"
          style={{
            opacity: nextPanelOpacity,
          }}
        >
          <GameItemImage
            src={nextItem.image}
            sources={nextItem.images}
            itemName={nextItem.name}
            gameTypeName={gameType.name}
            gameTypeSlug={gameType.slug}
            categorySlug={gameType.category.slug}
            fallbackClassName={`bg-gradient-to-tl ${gradient.from} ${gradient.to}`}
            priority
          />
          <div className="relative z-10 w-full max-w-xl text-center text-white">
            {!nextIsHousing && (
              <>
                <div className="mb-4 text-sm uppercase tracking-widest text-white/50">Guess</div>
                <h2 className="mb-3 text-2xl font-bold md:text-4xl">{nextItem.name}</h2>
                {nextDescription && (
                  <p className="mb-6 text-sm text-white/60 md:text-base">{nextDescription}</p>
                )}
              </>
            )}
            <HousingFacts
              floorAreaM2={nextItem.floorAreaM2}
              houseType={nextItem.houseType}
              location={nextItem.housingLocation}
              statusLabel="Guess the price"
            />

            {revealedValue !== null ? (
              <div className={`inline-block px-8 py-4 rounded-2xl backdrop-blur-xl border transition-all duration-500 animate-value-reveal ${
                gameState === "correct"
                  ? "bg-emerald-500/20 border-emerald-500/50"
                  : gameState === "wrong"
                    ? "bg-rose-500/20 border-rose-500/50"
                    : "bg-white/10 border-white/20"
              }`}>
                <div className="text-sm text-white/60 mb-1">{gameType.valueLabel}</div>
                <div className={`${valueTextSize} font-black ${
                  gameState === "correct" ? "text-emerald-400" : gameState === "wrong" ? "text-rose-400" : "text-white"
                }`}>
                  {animatedValue !== null ? displayValue(animatedValue) : displayValue(revealedValue)}
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => handleGuess("higher")}
                  disabled={gameState !== "playing"}
                  className="h-14 px-8 text-lg font-bold bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl shadow-lg shadow-emerald-700/20 gap-2 focus-visible:ring-4 focus-visible:ring-white/70"
                >
                  <ChevronUp className="h-6 w-6" />
                  Higher
                </Button>
                <Button
                  onClick={() => handleGuess("lower")}
                  disabled={gameState !== "playing"}
                  className="h-14 px-8 text-lg font-bold bg-rose-700 hover:bg-rose-800 text-white rounded-xl shadow-lg shadow-rose-700/20 gap-2 focus-visible:ring-4 focus-visible:ring-white/70"
                >
                  <ChevronDown className="h-6 w-6" />
                  Lower
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {gameState === "correct" && `Correct. Score ${score}.`}
        {gameState === "wrong" && `Incorrect. Final score ${score}.`}
      </div>
    </div>
  )
}
